import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft, EyeOff, Eye, Loader2, Plus, Pencil, Trash2, X, Check, ChevronRight,
} from "lucide-react";
import {
  fetchValidationManageKeys,
  fetchValidationManageKey,
  hideValidationValue,
  unhideValidationValue,
  addValidationValue,
  renameValidationValue,
  deleteValidationValue,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { errorMessage } from "@/lib/errors";

/**
 * Two-pane dropdown manager — much faster than rendering everything at once.
 * Left: list of keys with visible/hidden counts (single fast query).
 * Right: editor for the currently-selected key only (loads on demand).
 *
 * Caliber and Mvmnt MFG are auto-merged from the movements table — only
 * Hide/Restore is offered for those (Add/Rename/Delete must happen by
 * editing movements).
 *
 * Per user policy 2026-05-08: deleting/renaming a value never touches
 * existing watches/movements that reference it. Orphans stay; user fixes
 * them when they next service or sell that record.
 */
export default function ValidationManager() {
  const [keys, setKeys] = useState([]);
  const [keysLoading, setKeysLoading] = useState(false);
  const [activeKey, setActiveKey] = useState(null);
  const [keyData, setKeyData] = useState(null);
  const [keyLoading, setKeyLoading] = useState(false);
  const [busyValue, setBusyValue] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [filter, setFilter] = useState("");
  const [adding, setAdding] = useState(false);
  const [newVal, setNewVal] = useState("");

  const loadKeys = async (preserveActive = true) => {
    setKeysLoading(true);
    try {
      const r = await fetchValidationManageKeys();
      setKeys(r.keys || []);
      if (!preserveActive && r.keys?.length && !activeKey) {
        setActiveKey(r.keys[0].key);
      }
    } catch (err) {
      toast.error(errorMessage(err, "Could not load dropdowns"));
    } finally {
      setKeysLoading(false);
    }
  };

  const loadKey = async (k) => {
    if (!k) return;
    setKeyLoading(true);
    try {
      const r = await fetchValidationManageKey(k);
      setKeyData(r);
    } catch (err) {
      toast.error(errorMessage(err, `Could not load ${k}`));
    } finally {
      setKeyLoading(false);
    }
  };

  useEffect(() => { loadKeys(false); }, []);
  useEffect(() => { if (activeKey) loadKey(activeKey); }, [activeKey]);

  const onAdd = async () => {
    const v = newVal.trim();
    if (!v || !activeKey) return;
    setBusyValue("__add__");
    try {
      await addValidationValue(activeKey, v);
      toast.success(`Added "${v}"`);
      setNewVal(""); setAdding(false);
      await Promise.all([loadKey(activeKey), loadKeys(true)]);
    } catch (err) {
      toast.error(errorMessage(err, "Add failed"));
    } finally {
      setBusyValue(null);
    }
  };

  const onRename = async (oldVal, newVal) => {
    const v = newVal.trim();
    if (!v || v === oldVal || !activeKey) return;
    setBusyValue(oldVal);
    try {
      const r = await renameValidationValue(activeKey, oldVal, v);
      // The backend now cascades the rename across every collection/field
      // that references this dropdown. Sum the counts so we can tell the
      // user exactly how many records were updated.
      const total = Object.values(r?.cascaded || {}).reduce((s, n) => s + (Number(n) || 0), 0);
      toast.success(`Renamed to "${v}"`, {
        description: total > 0
          ? `Updated ${total} record${total === 1 ? "" : "s"} across the database.`
          : "No existing records used this value.",
      });
      await Promise.all([loadKey(activeKey), loadKeys(true)]);
    } catch (err) {
      toast.error(errorMessage(err, "Rename failed"));
    } finally {
      setBusyValue(null);
    }
  };

  const onToggleHidden = async (item) => {
    setBusyValue(item.value);
    try {
      if (item.hidden) {
        await unhideValidationValue(activeKey, item.value);
        toast.success(`Restored "${item.value}"`);
      } else {
        await hideValidationValue(activeKey, item.value);
        toast.success(`Hidden "${item.value}"`, {
          action: {
            label: "Undo",
            onClick: async () => {
              try { await unhideValidationValue(activeKey, item.value); loadKey(activeKey); loadKeys(true); }
              catch (err) { toast.error(errorMessage(err, "Undo failed")); }
            },
          },
        });
      }
      await Promise.all([loadKey(activeKey), loadKeys(true)]);
    } catch (err) {
      toast.error(errorMessage(err, "Toggle failed"));
    } finally {
      setBusyValue(null);
    }
  };

  const onDeleteConfirmed = async () => {
    if (!confirmDelete) return;
    const item = confirmDelete;
    setConfirmDelete(null);
    setBusyValue(item.value);
    try {
      await deleteValidationValue(activeKey, item.value);
      toast.success(`Deleted "${item.value}"`, {
        description: item.usage > 0
          ? `${item.usage} record${item.usage === 1 ? "" : "s"} still reference this — they keep the value but you won't see it in pickers.`
          : undefined,
      });
      await Promise.all([loadKey(activeKey), loadKeys(true)]);
    } catch (err) {
      toast.error(errorMessage(err, "Delete failed"));
    } finally {
      setBusyValue(null);
    }
  };

  const filteredItems = useMemo(() => {
    if (!keyData?.items) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return keyData.items;
    return keyData.items.filter((it) => String(it.value).toLowerCase().includes(q));
  }, [keyData, filter]);

  const activeMeta = keys.find((k) => k.key === activeKey);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 text-[#0A0604]" data-testid="validation-manager">
      <div className="flex items-center gap-3 mb-6">
        <Link
          to="/data"
          className="text-sm text-[#3A2A10] hover:text-[#0A0604] flex items-center gap-1"
          data-testid="back-to-data"
        >
          <ChevronLeft size={16} /> Data
        </Link>
        <h1 className="font-serif-display text-3xl">Dropdown Manager</h1>
      </div>

      <p className="text-sm text-[#3A2A10] mb-6 max-w-3xl">
        Pick a list to edit. <strong>Renaming</strong> a value cascades to every
        record using it (you'll see how many were updated). <strong>Deleting</strong> a
        value leaves the value on existing records — they keep the old text but
        the value disappears from pickers; you fix orphans next time you touch
        each record.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-6">
        {/* Left pane — list of keys */}
        <aside
          className="border border-[#8B6A1F]/35 rounded-lg bg-[#FBF3D9]/60 overflow-hidden h-fit md:sticky md:top-6"
          data-testid="vm-key-list"
        >
          <header className="px-4 py-3 border-b border-[#8B6A1F]/35 bg-[#FBF3D9]">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10]">
              Lookup tables
            </div>
          </header>
          {keysLoading && keys.length === 0 ? (
            <div className="px-4 py-3 flex items-center gap-2 text-sm text-[#3A2A10]">
              <Loader2 size={14} className="animate-spin" /> Loading…
            </div>
          ) : (
            <ul className="divide-y divide-[#8B6A1F]/15">
              {keys.map((k) => {
                const isActive = k.key === activeKey;
                return (
                  <li key={k.key}>
                    <button
                      onClick={() => setActiveKey(k.key)}
                      className={`w-full flex items-center justify-between px-4 py-2.5 text-left transition-colors ${
                        isActive ? "bg-[#D4AF37]/15" : "hover:bg-[#F5E9C5]/60"
                      }`}
                      data-testid={`vm-key-${k.key}`}
                    >
                      <div className="min-w-0">
                        <div className="font-serif-display text-base text-[#0A0604] truncate">
                          {k.key}
                        </div>
                        <div className="text-[10px] text-[#3A2A10]/80 font-mono-data">
                          {k.visible} visible{k.hidden ? ` · ${k.hidden} hidden` : ""}
                          {k.auto_merged && " · auto"}
                        </div>
                      </div>
                      <ChevronRight
                        size={14}
                        className={`text-[#8B6A1F] flex-shrink-0 transition-opacity ${
                          isActive ? "opacity-100" : "opacity-40"
                        }`}
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        {/* Right pane — editor for selected key */}
        <main className="border border-[#8B6A1F]/35 rounded-lg bg-[#FBF3D9]/60 overflow-hidden">
          {!activeKey ? (
            <div className="p-10 text-sm text-[#3A2A10]">Pick a list on the left to start editing.</div>
          ) : (
            <>
              <header className="flex items-center justify-between px-5 py-3 border-b border-[#8B6A1F]/35 bg-[#FBF3D9]">
                <div className="flex items-baseline gap-3">
                  <h2 className="font-serif-display text-2xl">{activeKey}</h2>
                  {activeMeta && (
                    <span className="text-xs text-[#3A2A10] font-mono-data">
                      {activeMeta.visible} visible{activeMeta.hidden ? ` · ${activeMeta.hidden} hidden` : ""}
                    </span>
                  )}
                  {keyData?.auto_merged_key && (
                    <span className="text-[10px] uppercase tracking-wider text-[#3A2A10] px-1.5 py-0.5 rounded bg-[#D4AF37]/20" title="Values come from the movements table">
                      auto-merged
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder="Filter…"
                    className="dark-input h-8 w-44"
                    data-testid="vm-filter"
                  />
                  {!keyData?.auto_merged_key && !adding && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAdding(true)}
                      className="h-8 border-[#8B6A1F]/45 bg-transparent text-[#3A2A10] hover:bg-[#2B2214]/[0.08] gap-1.5"
                      data-testid="vm-add"
                    >
                      <Plus size={13} /> Add
                    </Button>
                  )}
                </div>
              </header>

              {adding && !keyData?.auto_merged_key && (
                <form
                  onSubmit={(e) => { e.preventDefault(); onAdd(); }}
                  className="px-5 py-3 border-b border-[#8B6A1F]/20 flex items-center gap-2 bg-[#F5E9C5]/40"
                >
                  <Input
                    autoFocus
                    value={newVal}
                    onChange={(e) => setNewVal(e.target.value)}
                    placeholder={`New ${activeKey}…`}
                    className="dark-input h-9 flex-1"
                    data-testid="vm-add-input"
                  />
                  <Button type="submit" size="sm" className="h-9 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10]" disabled={!newVal.trim() || busyValue === "__add__"} data-testid="vm-add-confirm">
                    <Check size={14} className="mr-1" /> Add
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => { setAdding(false); setNewVal(""); }} className="h-9 text-[#3A2A10]">
                    <X size={14} />
                  </Button>
                </form>
              )}

              {keyLoading && !keyData ? (
                <div className="p-10 flex items-center gap-2 text-sm text-[#3A2A10]">
                  <Loader2 size={14} className="animate-spin" /> Loading {activeKey}…
                </div>
              ) : (
                <ul className="divide-y divide-[#8B6A1F]/15 max-h-[70vh] overflow-y-auto">
                  {filteredItems.length === 0 ? (
                    <li className="px-5 py-6 text-sm text-[#3A2A10]">No matches</li>
                  ) : filteredItems.map((it) => (
                    <Row
                      key={`${activeKey}:${it.value}`}
                      groupKey={activeKey}
                      item={it}
                      busy={busyValue === item_key(it)}
                      onRename={(newVal) => onRename(it.value, newVal)}
                      onDelete={() => setConfirmDelete(it)}
                      onToggleHidden={() => onToggleHidden(it)}
                    />
                  ))}
                </ul>
              )}
            </>
          )}
        </main>
      </div>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-[#FBF3D9] border-[#8B6A1F]/45">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-2xl text-[#0A0604]">
              Delete "{confirmDelete?.value}"?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#3A2A10]">
              {confirmDelete?.usage > 0 ? (
                <>
                  <strong>{confirmDelete.usage}</strong> record
                  {confirmDelete.usage === 1 ? "" : "s"} reference this value.
                  Those records keep "{confirmDelete.value}" stored on them —
                  it just disappears from the picker. You'll fix orphaned values
                  next time you service or sell each watch.
                </>
              ) : (
                <>This value is unused. It'll be removed from the picker entirely.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#8B6A1F]/45 bg-transparent text-[#3A2A10] hover:bg-[#2B2214]/[0.06]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={onDeleteConfirmed}
              className="bg-rose-600 hover:bg-rose-500 text-white"
              data-testid="vm-confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function item_key(it) { return it.value; }

function Row({ groupKey, item, busy, onRename, onDelete, onToggleHidden }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(item.value));

  const submit = (e) => {
    e?.preventDefault?.();
    if (!draft.trim() || draft === String(item.value)) {
      setEditing(false);
      setDraft(String(item.value));
      return;
    }
    onRename(draft.trim());
    setEditing(false);
  };

  return (
    <li
      className={`flex items-center justify-between px-5 py-2.5 hover:bg-[#F5E9C5]/40 transition-colors ${
        item.hidden ? "opacity-55" : ""
      }`}
      data-testid={`vm-row-${groupKey}-${item.value}`}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {editing ? (
          <form onSubmit={submit} className="flex items-center gap-2 flex-1">
            <Input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Escape") { setEditing(false); setDraft(String(item.value)); } }}
              className="dark-input h-8 max-w-xs"
              data-testid={`vm-rename-input-${groupKey}-${item.value}`}
            />
            <Button type="submit" size="sm" className="h-8 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10]" disabled={busy}>
              <Check size={14} />
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => { setEditing(false); setDraft(String(item.value)); }} className="h-8 text-[#3A2A10]">
              <X size={14} />
            </Button>
          </form>
        ) : (
          <>
            <span className="font-mono-data text-sm truncate">{String(item.value)}</span>
            {item.auto_merged && (
              <span className="text-[10px] uppercase tracking-wider text-[#3A2A10]/70 px-1.5 py-0.5 rounded bg-[#F5E9C5]" title="Owned by the movements table">
                from movements
              </span>
            )}
            <span className="text-xs text-[#3A2A10]/80">
              {item.usage} use{item.usage === 1 ? "" : "s"}
            </span>
            {item.hidden && (
              <span className="text-[10px] uppercase tracking-wider text-rose-700 px-1.5 py-0.5 rounded bg-rose-50">
                Hidden
              </span>
            )}
          </>
        )}
      </div>
      {!editing && (
        <div className="flex items-center gap-1 flex-shrink-0">
          {!item.auto_merged && (
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => setEditing(true)} className="text-[#3A2A10] hover:text-[#0A0604]" title="Rename" data-testid={`vm-rename-${groupKey}-${item.value}`}>
              <Pencil size={14} />
            </Button>
          )}
          <Button variant="ghost" size="sm" disabled={busy} onClick={onToggleHidden} className="text-[#3A2A10] hover:text-[#0A0604] gap-1" title={item.hidden ? "Restore (show in pickers)" : "Hide from pickers"} data-testid={`vm-toggle-${groupKey}-${item.value}`}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : item.hidden ? <Eye size={14} /> : <EyeOff size={14} />}
          </Button>
          {!item.auto_merged && (
            <Button variant="ghost" size="sm" disabled={busy} onClick={onDelete} className="text-rose-700 hover:text-rose-900 hover:bg-rose-50" title="Delete permanently" data-testid={`vm-delete-${groupKey}-${item.value}`}>
              <Trash2 size={14} />
            </Button>
          )}
        </div>
      )}
    </li>
  );
}
